import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { alpha, styled } from "@mui/material/styles";

import { colors } from "@/theme";

import {
  BANNER_ASPECT_RATIO,
  BANNER_ASPECT_RATIO_MOBILE,
  BANNER_ASPECT_RATIO_MOBILE_BREAKPOINT,
  BANNER_AUTOPLAY_INTERVAL,
  BANNER_TEXT_BAND,
  BANNER_TEXT_OVERLAY_GRADIENT,
  BANNER_TEXT_OVERLAY_GRADIENT_MOBILE,
} from "./data";

/** The night glass the controls sit on, since they sit on artwork. */
export const GLASS = "#07142E";
export const SKY = "#5BC8FF";

/** Lifts white text off bright patches of artwork the wash does not reach. */
export const BANNER_TEXT_SHADOW = `0 2px 12px ${alpha(GLASS, 0.6)}`;

const bannerAspectStyles = {
  aspectRatio: BANNER_ASPECT_RATIO,
  [`@media (max-width:${BANNER_ASPECT_RATIO_MOBILE_BREAKPOINT})`]: {
    aspectRatio: BANNER_ASPECT_RATIO_MOBILE,
  },
};

export const SliderTrack = styled(Box)(bannerAspectStyles);

export const Slide = styled(Box)(bannerAspectStyles, {
  position: "relative",
});

// An <img>, not a CSS background: the browser finds it early, can prioritise it and picks a size from srcSet.
export const SlideBackground = styled("img")({
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "100% center",
});

export const SlideGradient = styled(Box)(({ theme }) => ({
  position: "absolute",
  inset: 0,
  pointerEvents: "none",
  background: `${BANNER_TEXT_BAND.light}, ${BANNER_TEXT_OVERLAY_GRADIENT.light}`,
  [theme.breakpoints.down("sm")]: {
    background: BANNER_TEXT_OVERLAY_GRADIENT_MOBILE.light,
  },
  ...theme.applyStyles("dark", {
    background: `${BANNER_TEXT_BAND.dark}, ${BANNER_TEXT_OVERLAY_GRADIENT.dark}`,
    [theme.breakpoints.down("sm")]: {
      background: BANNER_TEXT_OVERLAY_GRADIENT_MOBILE.dark,
    },
  }),
}));

export const SlideContent = styled(Stack)(({ theme }) => ({
  position: "relative",
  justifyContent: "flex-end",
  height: "100%",
  alignItems: "flex-start",
  padding: 20,
  color: colors.white,
  textShadow: BANNER_TEXT_SHADOW,
  // On a phone the title is a chip in the top-left, where the phone wash is.
  [theme.breakpoints.down("sm")]: { padding: 12, justifyContent: "flex-start" },
}));

/** A small glass label on artwork: a game banner's name on a phone, a page hero's eyebrow. */
export const BannerChip = styled(Stack)({
  flexDirection: "row",
  alignItems: "center",
  gap: 6,
  padding: "4px 10px",
  borderRadius: 999,
  color: colors.white,
  backgroundColor: alpha(GLASS, 0.55),
  border: `1px solid ${alpha(SKY, 0.35)}`,
  backdropFilter: "blur(6px)",
  textShadow: "none",
});

/** The carousel: the track, the title over it, and its controls. */
export const BannerFrame = styled(Box)({
  position: "relative",
  overflow: "hidden",
  // The arrows come out for a pointer; on a touch screen the banner is swiped.
  "@media (hover: hover)": {
    "&:hover .banner-arrow, & .banner-arrow:focus-visible": { opacity: 1 },
    // While the pointer is on it the banner is not turning, so its clock stops.
    "&:hover .banner-dot span": { animationPlayState: "paused" },
  },
});

/** Previous and next, at the banner's sides. */
export const ArrowButton = styled("button", {
  shouldForwardProp: (prop) => prop !== "side",
})<{ side: "left" | "right" }>(({ theme, side }) => ({
  position: "absolute",
  top: "50%",
  [side]: 16,
  zIndex: 2,
  width: 40,
  height: 40,
  padding: 0,
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
  borderRadius: "50%",
  fontSize: 26,
  color: colors.white,
  backgroundColor: alpha(GLASS, 0.6),
  border: `1px solid ${alpha("#ffffff", 0.3)}`,
  backdropFilter: "blur(6px)",
  boxShadow: `0 4px 12px ${alpha("#000000", 0.35)}`,
  opacity: 0,
  transform: "translateY(-50%)",
  transition: "opacity 200ms ease, background-color 160ms ease, border-color 160ms ease",
  "&:hover": { backgroundColor: alpha(GLASS, 0.85), borderColor: SKY },
  "&:focus-visible": { outline: `2px solid ${SKY}`, outlineOffset: 2 },
  [theme.breakpoints.down("sm")]: { display: "none" },
  "@media (hover: none)": { display: "none" },
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
}));

/** The dots: one per picture, bottom right, clear of the title on the left. */
export const Dots = styled(Box)(({ theme }) => ({
  position: "absolute",
  right: 20,
  bottom: 16,
  zIndex: 2,
  display: "flex",
  alignItems: "center",
  padding: "0 4px",
  borderRadius: 999,
  backgroundColor: alpha(GLASS, 0.5),
  backdropFilter: "blur(6px)",
  [theme.breakpoints.down("sm")]: { right: "50%", bottom: 8, transform: "translateX(50%)" },
}));

/**
 * One dot. The picture that is up gets a longer one, which fills in the sky
 * blue over the time the picture has left, so the wait is something to see.
 */
export const Dot = styled("button", {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "still",
})<{ active: boolean; still: boolean }>(({ active, still }) => ({
  // The button is a 24px touch target (WCAG 2.5.8); the visible pill is drawn 8px inside it.
  position: "relative",
  width: (active ? 28 : 8) + 16,
  height: 24,
  padding: 0,
  border: 0,
  cursor: "pointer",
  borderRadius: 999,
  backgroundColor: "transparent",
  transition: "width 240ms ease",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 8,
    borderRadius: 999,
    backgroundColor: alpha("#ffffff", active ? 0.35 : 0.5),
    transition: "background-color 160ms ease",
  },
  "&:hover::before": { backgroundColor: alpha("#ffffff", 0.8) },
  "&:focus-visible": { outline: `2px solid ${SKY}`, outlineOffset: -2 },

  "& span": {
    position: "absolute",
    inset: 8,
    borderRadius: 999,
    backgroundColor: SKY,
    transformOrigin: "left center",
    // With nothing turning the banner by itself, the dot is simply lit.
    ...(still
      ? {}
      : {
          animation: `banner-dot-fill ${BANNER_AUTOPLAY_INTERVAL}ms linear forwards`,
          "@keyframes banner-dot-fill": {
            from: { transform: "scaleX(0)" },
            to: { transform: "scaleX(1)" },
          },
        }),
  },

  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
}));
